import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "components/card";
import Loader from "components/Loader";
import MonthFilter from "components/MonthFilter";
import { TokenUsageHistoryData, usageApi } from "api/usage";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const TokenUsageHistory = () => {
  const { apiKeyId } = useParams<{ apiKeyId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [historyData, setHistoryData] = useState<TokenUsageHistoryData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);

  // Month filter state
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    return dayjs().format("YYYY-MM");
  });

  const handleMonthChange = useCallback((month: string) => {
    setSelectedMonth(month);
  }, []);

  // Calculate start and end dates from selected month
  const getDateRangeFromMonth = useCallback((month: string) => {
    const startDate = dayjs(month).startOf("month").format("YYYY-MM-DD");
    const endDate = dayjs(month).endOf("month").format("YYYY-MM-DD");
    return { startDate, endDate };
  }, []);

  const fetchHistoryData = useCallback(async () => {
    // Only fetch if month and apiKeyId are valid
    if (!selectedMonth || !apiKeyId) {
      return;
    }

    try {
      setIsLoading(true);

      const { startDate, endDate } = getDateRangeFromMonth(selectedMonth);

      const data = await usageApi.getTokenUsageHistory({
        start_date: startDate,
        end_date: endDate,
        api_key_id: apiKeyId,
      });

      setHistoryData(data);
    } catch (err: any) {
      console.error("Error fetching token usage history:", err);
      toast.error(
        err.response?.data?.message ||
          t("usageOverview.history.errors.fetchHistory")
      );
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, apiKeyId, getDateRangeFromMonth, t]);

  useEffect(() => {
    if (apiKeyId) {
      fetchHistoryData();
    }
  }, [apiKeyId, fetchHistoryData]);

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("MM/DD/YYYY");
  };

  if (isLoading) {
    return <Loader txt={t("usageOverview.history.loading")} />;
  }

  if (!historyData) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-gray-600">
          {t("usageOverview.history.noData")}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t("usageOverview.historyHeader.title")}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t("usageOverview.history.apiKeyLabel", { id: apiKeyId })}
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          {t("common.back")}
        </button>
      </div>

      {/* Month Filter */}
      <MonthFilter
        selectedMonth={selectedMonth}
        onMonthChange={handleMonthChange}
        isLoading={isLoading}
        title={t("usageOverview.monthFilterTitle")}
        description={t("usageOverview.selectMonthHistory")}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card extra="p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.history.cards.totalTokens")}
          </h3>
          <div className="text-3xl font-bold text-blue-600">
            {historyData.total_tokens || 0}
          </div>
        </Card>
        <Card extra="p-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.history.cards.totalRequests")}
          </h3>
          <div className="text-3xl font-bold text-green-600">
            {historyData.total_requests || 0}
          </div>
        </Card>
      </div>

      {/* History Table */}
      <Card extra="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
            {t("usageOverview.history.table.title")}
          </h3>
          <p className="text-sm text-gray-600">
            {t("usageOverview.history.table.subtitle")}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
            <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="px-6 py-3">
                  {t("usageOverview.history.table.headers.date")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("usageOverview.history.table.headers.tokens")}
                </th>
                <th scope="col" className="px-6 py-3">
                  {t("usageOverview.history.table.headers.requests")}
                </th>
              </tr>
            </thead>
            <tbody>
              {historyData.items && historyData.items.length > 0 ? (
                historyData.items.map((item, index) => (
                  <tr
                    key={index}
                    className="border-b bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-600"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                      {formatDate(item.date)}
                    </td>
                    <td className="px-6 py-4">{item.tokens || 0}</td>
                    <td className="px-6 py-4">{item.requests || 0}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-8 text-center text-gray-500 dark:text-gray-400"
                  >
                    {t("usageOverview.history.table.empty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default TokenUsageHistory;
